import { v4 } from "uuid";

const convertTemplateInAnswer = async ({
  api,
  node,
  nodeData,
  campaignId,
  urlButtonLink,
  user,
  scenarioId,
  edges,
}) => {
  // 1. REMOVE ALL RELATED EDGES (buttons + main node) FIRST
  const removedButtonIds =
    node.data.buttons?.map((b) => b.gadgetId).filter(Boolean) || [];
  // Edges related to buttons
  if (Array.isArray(edges) && removedButtonIds.length) {
    for (const buttonId of removedButtonIds) {
      for (let i = edges.length - 1; i >= 0; i--) {
        if (edges[i].target === buttonId || edges[i].source === buttonId) {
          if (edges[i].data?.edgeId) {
            try {
              console.warn("Deleting edge (button)", edges[i].data.edgeId);
              await api.flowEdge.delete(edges[i].data.edgeId);
            } catch (e) {
              console.warn("Error deleting edge (button)", e);
            }
          }
          edges.splice(i, 1);
        }
      }
    }
  }
  // Edges related to the main node
  if (Array.isArray(edges) && node.data.nodeId) {
    for (let i = edges.length - 1; i >= 0; i--) {
      if (edges[i].source === node.id || edges[i].target === node.id) {
        if (edges[i].data?.edgeId) {
          try {
            console.warn("Deleting edge (template node)", edges[i].data.edgeId);
            await api.flowEdge.delete(edges[i].data.edgeId);
          } catch (e) {
            console.warn("Error deleting edge (template node)", e);
          }
        }
        edges.splice(i, 1);
      }
    }
  }

  // 2. BUSINESS LOGIC (delete nodes/replyButton, conversion, creation, update)

  // If a second URL button exists, remove it (limit: only one URL button).
  if (node.data.urlButton2) {
    console.warn("Deleting urlButton2");
    delete node.data.urlButton2;
  }

  // If a footer exists without a reply button, remove the footer (a footer requires at least one button).
  if (
    node.data.footer &&
    !node.data.buttons?.length &&
    !(node.data.urlButton?.text || node.data.urlButton?.url)
  ) {
    console.warn("Deleting footer because it requires at least one button");
    delete node.data.footer;
  }

  // Handle user conflict resolution choice between urlButton and buttons
  if (
    node.data.conflictResolutionChoice === "urlButton" &&
    node.data.buttons?.length
  ) {
    // Delete all reply buttons in backend
    for (const button of node.data.buttons) {
      if (button.gadgetId) {
        try {
          await api.flowNode.delete(button.gadgetId);
        } catch (e) {
          console.warn("Error deleting button gadgetId", e);
        }
      }
      if (button.replyButtonGadgetId) {
        try {
          await api.replyButton.delete(button.replyButtonGadgetId);
        } catch (e) {
          console.warn("Error deleting replyButton", e);
        }
      }
    }
    node.data.buttons = [];
  } else if (
    node.data.conflictResolutionChoice === "buttons" &&
    node.data.urlButton
  ) {
    delete node.data.urlButton;
  }

  // Replace {{n}} in the body with {{selected}} from bodyParameters (for display)
  if (node.data.body && Array.isArray(node.data.bodyParameters)) {
    node.data.body = node.data.body.replace(
      /\{\{\s*(\d+)\s*\}\}/g,
      (match, paramIndex) => {
        const index = parseInt(paramIndex, 10) - 1;
        const param = node.data.bodyParameters[index];
        return param ? `{{${param.selected}}}` : match;
      },
    );
  }

  // After applying constraints, convert the template to a message:
  // - Create a new message node on the backend
  // - Update the node data
  // - Delete the old template node

  console.warn("Converting template to message");
  nodeData = {
    id: node.data.messageSkeletonId,
    body: node.data.body ?? null,
    footer: node.data.footer ?? null,
    imageLink: node.data.headerParameters?.image?.link ?? null,
    videoLink: node.data.headerParameters?.video?.link ?? null,
    audioLink: node.data.headerParameters?.audio?.link ?? null,
    campaign: { _link: campaignId },
    isCampaignTrigger: null,
    urlButtonText: node.data.urlButton?.text
      ? node.data.urlButton.text.length > 20
        ? node.data.urlButton.text.substring(0, 20)
        : node.data.urlButton.text
      : null,
    urlButtonLink: node.data.urlButton ? urlButtonLink : null,
    isUrlButtonAtBottom: null,
    bodyParameters: Array.isArray(node.data.bodyParameters)
      ? node.data.bodyParameters.map(({ selected, default: def }) => ({
          selected,
          default: def,
        }))
      : null,
    whatsappTemplate: null,
  };
  console.warn("Node data", nodeData);

  try {
    if (node.data.whatsappTemplateId) {
      console.warn(
        "Deleting old whatsapp template",
        node.data.whatsappTemplateId,
      );
      await api.whatsappTemplate.delete(node.data.whatsappTemplateId);
    }
    if (node.data.nodeId) {
      console.warn("Deleting old template node", node.data.nodeId);
      await api.flowNode.delete(node.data.nodeId);
    }
  } catch (error) {
    console.warn("Error deleting old template node:", error);
  }

  console.warn("Creating new message node");
  const flowNodeRecord = await api.flowNode.create(
    {
      type: "message_skeleton",
      customer: { _link: user?.customerId },
      scenario: { _link: scenarioId },
      positionX: Math.round(node.position.x),
      positionY: Math.round(node.position.y),
      messageSkeleton: {
        update: nodeData,
      },
    },
    {
      select: {
        id: true,
        messageSkeleton: { id: true },
      },
    },
  );

  console.warn("Updating node data");
  node.type = "message";
  node.data.nodeId = flowNodeRecord.id;
  node.data.messageSkeletonId = flowNodeRecord.messageSkeleton.id;
  delete node.data.shouldBeConverted;

  console.warn("Updating buttons");
  if (node.data.buttons && node.data.buttons.length > 0) {
    for (const button of node.data.buttons) {
      if (button.gadgetId) {
        console.warn("Updating existing button", button.gadgetId);
        await api.flowNode.update(button.gadgetId, {
          flowNode: {
            parentNode: { _link: flowNodeRecord.id },
          },
        });
        console.warn("Updating reply button", button.replyButtonGadgetId);
        await api.replyButton.update(button.replyButtonGadgetId, {
          replyButton: {
            messageSkeleton: { _link: flowNodeRecord.messageSkeleton.id },
            title: button.title,
          },
        });
      } else {
        const id = v4();
        console.warn("Creating new reply button", id);
        const replyButtonNode = await api.flowNode.create({
          flowNode: {
            customer: { _link: user?.customerId },
            type: "reply_button",
            scenario: { _link: scenarioId },
            parentNode: { _link: flowNodeRecord.id },
            replyButton: {
              create: {
                messageSkeleton: { _link: flowNodeRecord.messageSkeleton.id },
                customer: { _link: user?.customerId },
                whatsappId: id,
                title: button.title,
              },
            },
          },
        });
        console.warn("Finding reply button node", replyButtonNode.id);
        const record = await api.flowNode.findById(replyButtonNode.id, {
          select: { replyButton: { id: true } },
        });

        button.gadgetId = replyButtonNode.id;
        button.replyButtonGadgetId = record.replyButton.id;
      }
    }
  }
};
export default convertTemplateInAnswer;
